/** Field-user invite modal — Phase 2D-sched-a.
 *
 * Opens from the SessionPage sidebar. Captures optional recipient
 * email + a "send email" checkbox; submits to
 * POST /v1/sessions/{id}/invite. When `send_email=true` and the email
 * is non-empty, the backend posts the invite via Django's mail
 * backend; the response carries the URL either way so the operator
 * can still copy + share manually.
 *
 * Role picker (2026-09-08): the backend has always accepted
 * role=field|observer|auditor (IssueInviteIn), but this modal
 * hardcoded "field" — there was no way to invite anyone else.
 * Reported feedback: multiple field participants ARE a supported,
 * intentional scenario (backlog #9.4 built proper multi-party
 * capture/signature targeting specifically for this), so nothing
 * here blocks inviting a 2nd/3rd field tech — but the role now
 * defaults to "observer" once a field participant is already
 * present, since adding an observer is the more common next step
 * than a redundant field invite. The operator can always override.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { BeneficiaryFormFields } from "@/components/BeneficiaryFormFields";
import {
  beneficiaryFormToSessionParticipant,
  emptyBeneficiaryForm,
  type BeneficiaryForm,
} from "@/components/beneficiaryForm";
import { ApiPaths, api } from "@/lib/api";
import { fetchOrgMembers } from "@/modules/dsr";
import type { SessionParticipant } from "@/modules/dsr";
import { fetchSessionParticipants } from "@/modules/dsr";
import type { Organization } from "@/lib/types";

type InviteRole = "field" | "observer" | "auditor";


type IssuedInvite = {
  url: string;
  token: string;
  /** 6-digit OTP — present when the invite was issued with at least
   * one of recipient_email/phone. Empty string for face-to-face URL
   * handoff. Server-side it's hashed; only the operator UI sees the
   * plain-text, and only on the issue response. */
  otp_code: string;
  invite: {
    id: string;
    recipient_first_name?: string;
    recipient_last_name?: string;
    recipient_email: string;
    recipient_phone: string;
    email_sent_at: string | null;
    sms_sent_at: string | null;
    expires_at: string;
  };
};


type Props = {
  sessionId: string;
  onClose: () => void;
  /** Opened from "Invite an observer": the role is decided by the
   *  action, so no picker, and the modal leads with the people already
   *  in the system rather than an empty email form. Reported
   *  2026-09-24. */
  mode?: "field" | "observer";
};


export function InviteFieldUserModal({
  sessionId,
  onClose,
  mode = "field",
}: Props) {
  const observerMode = mode === "observer";
  // Internal first: most observers are colleagues who already have an
  // account, so picking one from a list beats retyping their address.
  const [tab, setTab] = useState<"internal" | "external">("internal");
  const [pickedMemberId, setPickedMemberId] = useState<number | null>(null);
  const [memberQuery, setMemberQuery] = useState("");
  const { t } = useTranslation();
  // Same identity + legal-entity + address capture as a beneficiary; the
  // backend upserts a session_participant Contact from it (Phase 3).
  const [form, setForm] = useState<BeneficiaryForm>(emptyBeneficiaryForm);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [result, setResult] = useState<IssuedInvite | null>(null);

  // First name is required — drives the greeting + the resulting
  // Participant.display_name. Everything else is optional.
  const canSubmit = form.first_name.trim().length > 0;
  // On the Internal tab there is no form to fill — the requirement is
  // simply that somebody is selected.
  const canSubmitNow =
    observerMode && tab === "internal" ? pickedMemberId !== null : canSubmit;

  // Role picker — defaults to "observer" once a field participant is
  // already present (joined or still pending), since a 2nd field
  // invite is the less common next step; the operator can still pick
  // "field" explicitly (multiple field participants are fully
  // supported — see module docstring).
  const participants = useQuery({
    queryKey: ["session-participants", sessionId, "with-pending"],
    queryFn: () =>
      fetchSessionParticipants(sessionId, { includePending: true }),
  });
  const hasFieldAlready = (participants.data ?? []).some(
    (p: SessionParticipant) => p.role === "field" && !p.left_at,
  );
  const [role, setRole] = useState<InviteRole>(
    mode === "observer" ? "observer" : "field",
  );
  const [roleAutoSet, setRoleAutoSet] = useState(false);
  useEffect(() => {
    if (!roleAutoSet && participants.data && hasFieldAlready) {
      setRole("observer");
      setRoleAutoSet(true);
    }
  }, [participants.data, hasFieldAlready, roleAutoSet]);

  // Org SMS opt-in state — the toggle in /admin/retention. Disables the
  // SMS checkbox below when off.
  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });
  const smsAvailable = !!org.data?.sms_enabled;

  // People already in the org — the Internal tab. Only fetched in
  // observer mode; a field user is almost never a colleague.
  const members = useQuery({
    queryKey: ["org-members", "observer-picker"],
    enabled: observerMode,
    queryFn: () => fetchOrgMembers("active"),
  });
  // Only people who hold the `observer` role. The role exists per org
  // and is assigned in Members; inviting an inspector or an admin "as
  // an observer" was possible but read as a list of the wrong people.
  // When nobody holds it the list is empty on purpose — the empty state
  // says so rather than silently offering everyone.
  const memberRows = (members.data ?? []).filter(
    (m) =>
      m.is_active &&
      !m.erased &&
      (m.role_slugs ?? []).includes("observer"),
  );
  const visibleMembers = memberQuery.trim()
    ? memberRows.filter((m) =>
        `${m.first_name} ${m.last_name} ${m.email}`
          .toLowerCase()
          .includes(memberQuery.trim().toLowerCase()),
      )
    : memberRows;
  const pickedMember =
    memberRows.find((m) => m.user_id === pickedMemberId) ?? null;
  const internalPick = observerMode && tab === "internal";

  const issue = useMutation({
    mutationFn: async () => {
      // Internal observer: the person already exists, so the invite is
      // addressed to their account rather than to a retyped address.
      if (observerMode && tab === "internal" && pickedMember) {
        const res = await api.post<IssuedInvite>(
          ApiPaths.sessionInvite(sessionId),
          {
            role: "observer",
            recipient_first_name: pickedMember.first_name,
            recipient_last_name: pickedMember.last_name,
            recipient_email: pickedMember.email,
            send_email: sendEmail,
            send_sms: false,
          },
        );
        return res.data;
      }
      const p = beneficiaryFormToSessionParticipant(form);
      const res = await api.post<IssuedInvite>(
        ApiPaths.sessionInvite(sessionId),
        {
          role,
          recipient_first_name: p.first_name,
          recipient_last_name: p.last_name,
          recipient_email: p.email,
          recipient_phone: p.phone,
          address_line: p.address_line,
          city: p.city,
          postal_code: p.postal_code,
          address_country: p.address_country,
          is_business: p.is_business,
          legal_name: p.legal_name,
          registration_number: p.registration_number,
          vat_id: p.vat_id,
          company_contact_name: p.company_contact_name,
          company_contact_email: p.company_contact_email,
          company_contact_phone: p.company_contact_phone,
          send_email: !!p.email && sendEmail,
          send_sms: !!p.phone && sendSms && smsAvailable,
        },
      );
      return res.data;
    },
    onSuccess: setResult,
  });

  return (
    <div
      className="modal-backdrop bg-black/60"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md space-y-3 rounded-lg border border-nb-border bg-nb-bg p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-nb-text">
            {observerMode
              ? t("session.inviteObserver", "Invite an observer")
              : t("ui.inviteFieldUserModal.title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-nb-text-3 hover:text-nb-text"
            aria-label={t("ui.inviteFieldUserModal.close")}
          >
            ×
          </button>
        </header>

        {!result ? (
          <>
            {/* Tabs, observer mode only. Internal leads because most
                observers are colleagues who already have an account —
                picking one beats retyping their address, and gets it
                right. Reported 2026-09-24. */}
            {observerMode && (
              <div
                className="flex gap-1 rounded-md border border-nb-border p-1"
                role="tablist"
              >
                {(["internal", "external"] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={tab === k}
                    onClick={() => setTab(k)}
                    className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition ${
                      tab === k
                        ? "bg-nb-mint/15 text-nb-mint"
                        : "text-nb-text-3 hover:text-nb-text"
                    }`}
                  >
                    {k === "internal"
                      ? t("session.observerInternal", "Internal")
                      : t("session.observerExternal", "External")}
                  </button>
                ))}
              </div>
            )}

            {observerMode && tab === "internal" && (
              <div className="space-y-2">
                <input
                  type="search"
                  className="input w-full text-xs"
                  placeholder={t(
                    "session.observerSearchMembers",
                    "Search people in your organization",
                  )}
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                />
                {members.isLoading && (
                  <p className="text-xs text-nb-text-3">{t("common.loading")}</p>
                )}
                {/* Two different empty states: nobody holds the role at
                    all, versus the search filtering everyone out. They
                    call for different actions. */}
                {!members.isLoading && memberRows.length === 0 && (
                  <p className="text-xs text-nb-text-3">
                    {t(
                      "session.observerNoneInOrg",
                      "No observer found in the organization. Give someone the Observer role in Members, or use the External tab.",
                    )}
                  </p>
                )}
                {!members.isLoading &&
                  memberRows.length > 0 &&
                  visibleMembers.length === 0 && (
                    <p className="text-xs text-nb-text-3">
                      {t(
                        "session.observerNoMembers",
                        "Nobody in your organization matches. Use the External tab to invite someone by email.",
                      )}
                    </p>
                  )}
                <ul
                  className="max-h-56 space-y-1 overflow-y-auto"
                  data-testid="observer-member-list"
                >
                  {visibleMembers.map((m) => {
                    const name =
                      `${m.first_name} ${m.last_name}`.trim() || m.email;
                    const picked = pickedMemberId === m.user_id;
                    return (
                      <li key={m.user_id}>
                        <button
                          type="button"
                          onClick={() => setPickedMemberId(m.user_id)}
                          aria-pressed={picked}
                          className={`flex w-full items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition ${
                            picked
                              ? "border-nb-mint/60 bg-nb-mint/10 text-nb-text"
                              : "border-nb-border/60 text-nb-text-2 hover:border-nb-mint/40"
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium">
                              {name}
                            </span>
                            <span className="block truncate text-nb-text-3">
                              {m.email}
                            </span>
                          </span>
                          {picked && (
                            <span className="shrink-0 text-nb-mint">✓</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* The role picker is for the generic invite flow. Opened as
                "Invite an observer", the action has already decided the
                role — asking again invites a wrong answer. */}
            {!observerMode && (
            <label className="block text-xs text-nb-text-2">
              <span>{t("ui.inviteFieldUserModal.roleLabel")}</span>
              <select
                className="input mt-1 w-full"
                value={role}
                onChange={(e) => {
                  setRole(e.target.value as InviteRole);
                  setRoleAutoSet(true); // operator's explicit pick wins from here
                }}
              >
                <option value="field">
                  {t("ui.inviteFieldUserModal.roleField")}
                </option>
                <option value="observer">
                  {t("ui.inviteFieldUserModal.roleObserver")}
                </option>
                <option value="auditor">
                  {t("ui.inviteFieldUserModal.roleAuditor")}
                </option>
              </select>
              {hasFieldAlready && role === "field" && (
                <span className="mt-1 block text-[11px] text-amber-400">
                  {t("ui.inviteFieldUserModal.roleFieldAlreadyPresentHint")}
                </span>
              )}
            </label>
            )}

            {/* Identity form — for a field invite, or an EXTERNAL
                observer who has no account to pick from. */}
            {(!observerMode || tab === "external") && (
              <BeneficiaryFormFields
                form={form}
                onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                customFields={{}}
                onCustomFieldChange={() => {}}
                hideCustomFields
              />
            )}

            {/* Delivery channels — gated on the identity email / phone. */}
            {/* On the Internal tab the address comes from the selected
                account, so "(enter an email first)" was nonsense and the
                box sat disabled with no form to fill. SMS has no source
                at all there — we never collect a colleague's number. */}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={internalPick ? !pickedMember : !form.email.trim()}
              />
              <span className="text-nb-text-2">
                {t("ui.inviteFieldUserModal.sendEmail")}
                {!internalPick &&
                  !form.email.trim() &&
                  t("ui.inviteFieldUserModal.sendEmailHint")}
              </span>
            </label>
            {!internalPick && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sendSms && smsAvailable}
                  onChange={(e) => setSendSms(e.target.checked)}
                  disabled={!form.phone.trim() || !smsAvailable}
                />
                <span className="text-nb-text-2">
                  {t("ui.inviteFieldUserModal.sendSms")}
                  {!form.phone.trim() && t("ui.inviteFieldUserModal.sendSmsNoPhone")}
                  {form.phone.trim() && !smsAvailable && t("ui.inviteFieldUserModal.sendSmsNotAvailable")}
                </span>
              </label>
            )}
            {issue.isError && (
              <p className="text-xs text-cvp-danger">
                {(issue.error as Error).message ?? t("ui.inviteFieldUserModal.errorGeneric")}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={onClose}
              >
                {t("ui.inviteFieldUserModal.cancel")}
              </button>
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => issue.mutate()}
                disabled={issue.isPending || !canSubmitNow}
                data-testid="invite-submit"
                title={
                  !canSubmitNow && !observerMode
                    ? t("ui.inviteFieldUserModal.firstNameRequired")
                    : undefined
                }
              >
                {issue.isPending ? t("ui.inviteFieldUserModal.issuing") : t("ui.inviteFieldUserModal.issueBtn")}
              </button>
            </div>
          </>
        ) : (
          <ResultPanel result={result} onClose={onClose} />
        )}
      </div>
    </div>
  );
}


function ResultPanel({
  result,
  onClose,
}: {
  result: IssuedInvite;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <p className="text-sm text-nb-text">
        {t("ui.inviteFieldUserModal.resultIssued")}
        {(result.invite.email_sent_at || result.invite.sms_sent_at) && t("ui.inviteFieldUserModal.sentVia")}
        {result.invite.email_sent_at && (
          <>
            {t("ui.inviteFieldUserModal.channelEmail")} (<strong>{result.invite.recipient_email}</strong>)
            {result.invite.sms_sent_at && " + "}
          </>
        )}
        {result.invite.sms_sent_at && (
          <>
            {t("ui.inviteFieldUserModal.channelSms")} (<strong>{result.invite.recipient_phone}</strong>)
          </>
        )}
        .
      </p>
      <label className="block space-y-1 text-sm">
        <span className="text-nb-text-2">
          {t("ui.inviteFieldUserModal.joinUrlLabel")}
        </span>
        <input className="input w-full" readOnly value={result.url} />
      </label>
      {result.otp_code && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
          <div className="text-xs font-medium uppercase tracking-wider text-amber-300">
            {t("ui.inviteFieldUserModal.otpLabel")}
          </div>
          <div className="mt-1 flex items-center gap-3">
            <code className="font-mono text-xl tracking-[0.3em] text-nb-text">
              {result.otp_code}
            </code>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => navigator.clipboard?.writeText(result.otp_code)}
            >
              {t("ui.inviteFieldUserModal.copy")}
            </button>
          </div>
          <p className="mt-2 text-xs text-nb-text-2">
            {t("ui.inviteFieldUserModal.otpSharedVia")}{" "}
            {result.invite.email_sent_at ? t("ui.inviteFieldUserModal.channelEmail") : ""}
            {result.invite.email_sent_at && result.invite.sms_sent_at && " + "}
            {result.invite.sms_sent_at ? t("ui.inviteFieldUserModal.channelSms") : ""}
            {!result.invite.email_sent_at && !result.invite.sms_sent_at &&
              t("ui.inviteFieldUserModal.otpManualHandoff")}
            {t("ui.inviteFieldUserModal.otpExpirySuffix")}
          </p>
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1 text-sm"
          onClick={() => navigator.clipboard?.writeText(result.url)}
        >
          {t("ui.inviteFieldUserModal.copyLink")}
        </button>
        <button
          type="button"
          className="btn-ghost flex-1 text-sm"
          onClick={onClose}
        >
          {t("ui.inviteFieldUserModal.done")}
        </button>
      </div>
      <p className="text-[10px] text-nb-text-3">
        {t("ui.inviteFieldUserModal.expiresAt", { date: formatDateTime(result.invite.expires_at) })}
      </p>
    </>
  );
}
